import {
  CircleText,
  QuestionCircle,
  QuestionDisplayMode,
} from '@doltech/ui/lib/figma/CircleText/CircleText';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import QuestionConnector from '@doltech/ui/lib/figma/Question/QuestionConnector';
import { QuestionBox } from '@doltech/ui/lib/figma/Question/QuestionDropable';
import { QuestionV2 } from '@doltech/ui/lib/figma/Question/QuestionV2';
import renderNode, { Div } from '@doltech/ui/lib/figma/renderNode';
import { MarkedTypeEnum, ReadingViewModeEnum } from '@doltech/utils/lib/constants';
import { numberToABC } from '@doltech/utils/lib/text';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useMount } from 'react-use';
import styled from 'styled-components';

import { useTestSubmissionStore } from '../../../model/test-submissions.hook';

interface QuestionDropableProps {
  no: string | number;
  title: string | number;
  onItemClick?: any;
  userAnswers?: any;
  index?: number;
  options?: any;
  numberToString?: any;
}

const Main = styled.div`
  position: relative;
  font-weight: 500;
  &:not(:last-child) {
    margin-bottom: 24px;
  }
  .question {
    display: flex;
    margin-bottom: 8px;
    align-items: center;
    .question-no {
      margin-right: 12px;
    }
  }

  .question-connector-dropable {
    display: grid;
    position: relative;
    grid-template-columns: 43px 1fr;
    padding-left: 32px;
    gap: 4px;
  }

  .question-dropable-list {
    padding-top: 20px;
    position: relative;
  }

  &.${MarkedTypeEnum.CORRECT} {
    .question .question-do-test {
      background-color: ${colorsV2.green100};
    }
  }
  &.${MarkedTypeEnum.WRONG} {
    .question .question-do-test {
      background-color: ${colorsV2.red100};
    }
  }
  &.${MarkedTypeEnum.MISSING} {
    .question .question-do-test {
      background-color: ${colorsV2.black5};
      > span {
        color: ${colorsV2.black100};
      }
    }
  }
  .empty-drop-box {
    .sub-text {
      font-size: 14;
      font-weight: 500;
    }
  }
`;

export const QuestionMatchingMobile = observer((props: QuestionDropableProps) => {
  const { no, index, title, onItemClick, userAnswers, options, numberToString } = props;
  const questionDropableRefs = React.useRef<any>({});
  const [nodeOffset, setNodeOffset] = React.useState([]);
  const answer = userAnswers[index];
  const opt = options[answer];
  const testSubmissionStore = useTestSubmissionStore();

  const currentViewMode = testSubmissionStore.currentTestSection?.currentViewMode;

  const currentNode = React.useMemo(() => {
    if (Number.isFinite(answer)) {
      return {
        color: colorsV2.blue100,
        renderElement: (
          <QuestionBox>
            <CircleText
              displayMode={QuestionDisplayMode.QUESTION_OPTION}
              circleText={numberToString ? numberToString(opt?.key + 1) : numberToABC(opt?.key + 1)}
              text={get(opt, 'text', opt.value)}
              selected
            />
          </QuestionBox>
        ),
      };
    }
    return {
      color: colorsV2.gray20,
      renderElement: (
        <div>
          <QuestionBox>
            <div className="row empty-drop-box">
              <QuestionCircle
                className={QuestionDisplayMode.QUESTION_OPTION}
                style={{ marginRight: 0 }}
              />
              <Typography.Text variant="regular/14" style={{ marginLeft: 12 }}>
                bấm để chọn đáp án
              </Typography.Text>
            </div>
          </QuestionBox>
        </div>
      ),
    };
  }, [answer, numberToString, opt]);

  const calculateLayout = React.useCallback(() => {
    if (questionDropableRefs.current) {
      setNodeOffset([
        {
          y: questionDropableRefs.current?.offsetTop || 0,
          height: questionDropableRefs.current?.clientHeight || 0,
        },
      ]);
    }
  }, []);

  React.useEffect(() => {
    if (currentViewMode === ReadingViewModeEnum.QUESTION) {
      calculateLayout();
    }
  }, [calculateLayout, currentViewMode]);

  useMount(() => {
    calculateLayout();
  });

  return (
    <Main className="question-dropable-container">
      <div className="question">
        <QuestionV2 no={no} id={`question-no-${no}`} />
        <QuestionBox>{`${title}`}</QuestionBox>
      </div>
      <div className="question-connector-dropable">
        <div className="question-connector">
          <QuestionConnector
            nodes={[
              {
                color: currentNode.color,
                y: nodeOffset[0]?.y,
                height: nodeOffset[0]?.height || 0,
              },
            ]}
          />
        </div>
        <div className="question-dropable-list">
          <div
            ref={questionDropableRefs}
            aria-hidden
            onKeyDown={(e) => e.preventDefault()}
            onClick={onItemClick}
            className="question-dropable"
          >
            {renderNode(Div, currentNode.renderElement, props)}
          </div>
        </div>
      </div>
    </Main>
  );
});
